import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { X } from 'lucide-react'

const COOKIE_CONSENT_KEY = 'yamp-cookie-consent'

type ConsentStatus = 'pending' | 'accepted' | 'declined'

export function CookieConsent() {
  const { i18n } = useTranslation()
  const [status, setStatus] = useState<ConsentStatus>('pending')
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY)
    if (stored === 'accepted' || stored === 'declined') {
      setStatus(stored as ConsentStatus)
    } else {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => setIsVisible(true), 1000)
      return () => clearTimeout(timer)
    }
  }, [])

  const handleAccept = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted')
    setStatus('accepted')
    setIsVisible(false)
  }

  const handleDecline = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'declined')
    setStatus('declined')
    setIsVisible(false)
  }

  if (status !== 'pending' || !isVisible) {
    return null
  }

  const isJapanese = i18n.language === 'ja'

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-md z-50 animate-in slide-in-from-bottom-4 duration-300">
      <div className="bg-bg-surface/95 backdrop-blur-md border border-white/10 rounded-2xl p-4 shadow-2xl">
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <h3 className="font-body-en text-sm font-semibold text-white mb-1">
              {isJapanese ? 'Cookieの使用について' : 'Cookie Notice'}
            </h3>
            <p className="font-body-ja text-xs text-white/60 leading-relaxed">
              {isJapanese
                ? '当サイトでは、サービス向上のためにCookieを使用しています。「同意する」をクリックすると、Cookieの使用に同意したことになります。'
                : 'We use cookies to enhance your experience. By clicking "Accept", you consent to our use of cookies for analytics and service improvement.'}
            </p>
            <a
              href="/privacy"
              className="font-body-en text-xs text-brand-accent hover:underline mt-1 inline-block"
            >
              {isJapanese ? '詳細を見る' : 'Learn more'}
            </a>
          </div>
          <button
            onClick={handleDecline}
            className="text-white/40 hover:text-white/60 transition-colors p-1"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex gap-2 mt-3">
          <Button
            variant="outline"
            size="sm"
            onClick={handleDecline}
            className="flex-1 text-xs"
          >
            {isJapanese ? '必要なもののみ' : 'Necessary only'}
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={handleAccept}
            className="flex-1 text-xs bg-brand-accent hover:bg-brand-accent/90"
          >
            {isJapanese ? '同意する' : 'Accept all'}
          </Button>
        </div>
      </div>
    </div>
  )
}

// Hook to check cookie consent status
export function useCookieConsent(): ConsentStatus {
  const [status, setStatus] = useState<ConsentStatus>('pending')

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY)
    if (stored === 'accepted' || stored === 'declined') {
      setStatus(stored as ConsentStatus)
    }
  }, [])

  return status
}
